// Performance Metrics Card - Admin Dashboard
// Shows key portfolio-wide performance statistics

import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { TrendingUp, TrendingDown, EmojiEvents, ShowChart, AttachMoney } from '@mui/icons-material';
import { Series } from '../../types';
import { formatCurrency, formatPercentage } from '../../utils/navCalculations';

interface PerformanceMetricsCardProps {
  series: Series[];
  hurdleRate?: number;
}

const PerformanceMetricsCard: React.FC<PerformanceMetricsCardProps> = ({
  series,
  hurdleRate = 3, // Default 3% hurdle rate
}) => {
  const activeSeries = series.filter(s => s.status === 'active');

  if (activeSeries.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#FFD700' }}>
            📊 Performance Metrics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No active series to analyze
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Calculate returns for each series
  const seriesReturns = activeSeries.map(s => ({
    id: s.id,
    lenderId: s.lenderId,
    returns: ((s.currentNAV - s.principalAmount) / s.principalAmount) * 100,
    principal: s.principalAmount,
    currentNAV: s.currentNAV,
  }));

  // 1. Average return across all series
  const averageReturn = seriesReturns.reduce((sum, s) => sum + s.returns, 0) / seriesReturns.length;

  // 2. Best performing series
  const bestSeries = seriesReturns.reduce((best, current) =>
    current.returns > best.returns ? current : best
  );

  // 3. Worst performing series
  const worstSeries = seriesReturns.reduce((worst, current) =>
    current.returns < worst.returns ? current : worst
  );

  // 4. Total AUM growth
  const totalPrincipal = activeSeries.reduce((sum, s) => sum + s.principalAmount, 0);
  const totalCurrentNAV = activeSeries.reduce((sum, s) => sum + s.currentNAV, 0);
  const totalGrowth = totalCurrentNAV - totalPrincipal;
  const totalGrowthPercent = (totalGrowth / totalPrincipal) * 100;

  // 5. Number of series beating hurdle rate
  const seriesBeatingHurdle = seriesReturns.filter(s => s.returns > hurdleRate).length;

  const MetricBox = ({ icon: Icon, label, value, subtitle, color }: any) => (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'rgba(255, 215, 0, 0.05)',
        border: '1px solid rgba(255, 215, 0, 0.2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon sx={{ fontSize: 20, color: color || '#FFD700' }} />
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="h5" sx={{ color: color || '#FFF', fontWeight: 600 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: '#FFD700', mb: 3 }}>
          📊 Performance Metrics
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {/* Average Return */}
          <MetricBox
            icon={ShowChart}
            label="Average Return"
            value={formatPercentage(averageReturn / 100)}
            subtitle={`Across ${activeSeries.length} series`}
            color={averageReturn >= 0 ? '#4CAF50' : '#F44336'}
          />

          {/* Best Performing Series */}
          <MetricBox
            icon={EmojiEvents}
            label="Best Performing"
            value={formatPercentage(bestSeries.returns / 100)}
            subtitle={`Series ${bestSeries.id.slice(0, 8)}...`}
            color="#4CAF50"
          />

          {/* Worst Performing Series */}
          <MetricBox
            icon={TrendingDown}
            label="Worst Performing"
            value={formatPercentage(worstSeries.returns / 100)}
            subtitle={`Series ${worstSeries.id.slice(0, 8)}...`}
            color={worstSeries.returns >= 0 ? '#FFD700' : '#F44336'}
          />

          {/* Total AUM Growth */}
          <MetricBox
            icon={AttachMoney}
            label="Total AUM Growth"
            value={formatCurrency(totalGrowth)}
            subtitle={`${formatPercentage(totalGrowthPercent / 100)} overall`}
            color={totalGrowth >= 0 ? '#4CAF50' : '#F44336'}
          />

          {/* Series Beating Hurdle */}
          <MetricBox
            icon={TrendingUp}
            label={`Beating ${hurdleRate}% Hurdle`}
            value={`${seriesBeatingHurdle} / ${activeSeries.length}`}
            subtitle={formatPercentage(seriesBeatingHurdle / activeSeries.length)}
            color={seriesBeatingHurdle > activeSeries.length / 2 ? '#4CAF50' : '#FFD700'}
          />

          {/* Total AUM */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'rgba(255, 215, 0, 0.1)',
              border: '2px solid #FFD700',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              TOTAL AUM
            </Typography>
            <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 600 }}>
              {formatCurrency(totalCurrentNAV)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#4CAF50' }}>
              Principal: {formatCurrency(totalPrincipal)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PerformanceMetricsCard;
